import currency from "currency.js";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  InstrumentTypeResponse,
  OptionTypeResponse,
  SideResponse,
} from "../../../codegen-api";
import { MarketContext } from "../../../contexts/MarketContext";
import { MarketInstrumentContext } from "../../../contexts/MarketInstrumentContext";

import { COMPONENTS } from "../../../constants/design/spacing";
import { useOrder } from "../../../hooks/api/order/useOrder";
import useTickersWSS from "../../../hooks/wss/tickers/useTickersWSS";
import useIndexWSS from "../../../hooks/wss/useIndexWSS";
import usePositionsWSS from "../../../hooks/wss/usePositionsWSS";
import MobileTradeFormModal from "../../MobileTradeFormModal";
import DateSelection from "../DateSelection";
import { ITableContentRow } from "../OptionsTable";
import { OptionsChainContainer, Padding } from "../style";
import MobileOptionsChainContent from "./MobileOptionsChainContent";
import MobileOptionsChainTabs from "./MobileOptionsChainTabs";
import { IMobileOptionsChainProps } from "./types";

function MobileOptionsChain({
  scrollableContainerRef,
}: IMobileOptionsChainProps) {
  const bottomBarRef = useRef<HTMLDivElement>(null);

  // As long as bottom bar tabs covers over half the screen, its considered showing
  const isBottomBarShowing =
    (bottomBarRef.current?.getBoundingClientRect().top || Infinity) <=
    document.body.clientHeight - COMPONENTS.mobileTradeBottomBarMinHeight;

  const { market } = useContext(MarketContext);

  const {
    selectedValues,
    expiries,
    selectedOptionInstrument: selectedInstrument,
    setExpiry,
    setOrderType,
    setStrike,
    orderTypes,
    reset,
    marketsData,
  } = useContext(MarketInstrumentContext);

  const { expiry } = selectedValues;

  const marketsOfThisExpiry = useMemo(
    () => marketsData?.filter((v) => Number(v.expiry) === expiry) || [],
    [expiry, marketsData]
  );

  const putCalls = useMemo(
    () => ({
      calls: marketsOfThisExpiry.filter(
        (v) => v.option_type === OptionTypeResponse.Call
      ),
      puts: marketsOfThisExpiry.filter(
        (v) => v.option_type === OptionTypeResponse.Put
      ),
    }),
    [marketsOfThisExpiry]
  );

  const { data: orders } = useOrder();

  // Websocket
  const { instrumentTicker } = useTickersWSS(
    market.asset,
    InstrumentTypeResponse.Option,
    expiry
  );
  const { positions, positionsMap } = usePositionsWSS();
  const { index } = useIndexWSS(market.asset);
  const currentIndexPrice = index?.price;

  const [optionTypeFilter, setOptionTypeFilter] = useState<OptionTypeResponse>(
    OptionTypeResponse.Call
  );
  const [side, setSide] = useState<SideResponse>(SideResponse.Buy);
  const [showTradeForm, setShowTradeForm] = useState(false);

  const onTrade = useCallback(
    (orderType: OptionTypeResponse, exp: number, strike: string) => {
      setOrderType(orderType);
      setExpiry(exp);
      setStrike(strike);
      setShowTradeForm(true);
    },
    [setExpiry, setOrderType, setStrike]
  );

  const onSelectExpiry = useCallback(
    (expiryDate: number) => {
      setExpiry(expiryDate);
    },
    [setExpiry]
  );

  const onSelectOptionTypeFilterMobile = useCallback((value: string) => {
    setOptionTypeFilter(value as OptionTypeResponse);
  }, []);

  const onScrollToPortfolioTray = useCallback(() => {
    if (isBottomBarShowing) {
      scrollableContainerRef?.current?.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      return;
    }

    bottomBarRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [isBottomBarShowing, scrollableContainerRef]);

  const decoratedExpiries = useMemo(() => {
    const withPositions =
      positions
        .filter((pos) => !!pos.option)
        .map((pos) => pos.option!.expiry) ?? [];
    const withOrders =
      orders?.filter((o) => !!o.expiry).map((o) => o.expiry!) ?? [];
    return {
      withPositions,
      withOrders,
    };
  }, [orders, positions]);

  // Returns rows, split into below strike price and above strike price
  const filteredRows = useMemo(() => {
    const { calls, puts } = putCalls;

    // Finally, split into 2 arrays, less than and more than current price
    const lessThanCurrPriceRows: ITableContentRow[] = [];
    const moreThanCurrPriceRows: ITableContentRow[] = [];

    if (currentIndexPrice) {
      const currentOptions =
        optionTypeFilter === OptionTypeResponse.Call ? calls : puts;
      currentOptions.forEach((m) => {
        const ticker = instrumentTicker[m.instrument_name];
        if (m.strike && ticker) {
          const position = positionsMap[m.instrument_name];
          const row: ITableContentRow = {
            strike: m.strike,
            expiry: Number(m.expiry ?? "0"),
            ticker: {
              delta: ticker.mark?.greeks?.delta || "0",
              open_interest: ticker.open_interest || "0",
              bid: {
                price: ticker.bid?.price || "0",
                volume: ticker.bid?.amount || "0",
                iv: ticker.bid?.greeks?.iv || "0",
                orderVolume: Number(
                  orders?.find(
                    (o) =>
                      o.instrument_id === ticker.instrument_id &&
                      o.side === SideResponse.Buy
                  )?.amount || 0
                ),
              },
              ask: {
                price: ticker.ask?.price || "0",
                volume: ticker.ask?.amount || "0",
                iv: ticker.ask?.greeks?.iv || "0",
                orderVolume: Number(
                  orders?.find(
                    (o) =>
                      o.instrument_id === ticker.instrument_id &&
                      o.side === SideResponse.Sell
                  )?.amount || 0
                ),
              },
              instrumentName: ticker.instrument_name,
              instrumentId: Number(ticker.instrument_id),
              position: Number(position?.amount || 0),
              side: position?.side || SideResponse.Buy,
              // Omitted mark stays empty (renders "-"), never a "0" quote.
              markPrice: ticker.mark?.price ?? "",
              iv: ticker.mark?.greeks?.iv || "0",
            },
          };

          if (currency(row.strike || 0).value <= Number(currentIndexPrice)) {
            lessThanCurrPriceRows.push(row);
          } else {
            moreThanCurrPriceRows.push(row);
          }
        }
      });
    }

    // Sorted by strike
    return {
      lessThanCurrPriceRows: lessThanCurrPriceRows.sort(
        (a, b) => Number(a.strike) - Number(b.strike)
      ),
      moreThanCurrPriceRows: moreThanCurrPriceRows.sort(
        (a, b) => Number(a.strike) - Number(b.strike)
      ),
    };
  }, [
    putCalls,
    instrumentTicker,
    optionTypeFilter,
    positionsMap,
    currentIndexPrice,
    orders,
  ]);

  // Whenever market changed, reset trade values
  useEffect(() => {
    reset();
  }, [market, reset]);

  // For initial loads where expiriesData gets updated and no expiry, set default expiry
  useEffect(() => {
    if (expiries.length && !expiry) {
      setExpiry(expiries[0]);
      setOrderType(orderTypes[0]);
    }
  }, [expiries, expiry, setExpiry, reset, setOrderType, orderTypes]);

  return (
    <OptionsChainContainer>
      <Padding>
        <MobileOptionsChainTabs
          side={side}
          setSide={setSide}
          optionTypeFilter={optionTypeFilter}
          onSelectOptionTypeFilterMobile={onSelectOptionTypeFilterMobile}
        />

        <DateSelection
          expiries={expiries}
          selectedExpiry={expiry}
          expiriesWithPosition={decoratedExpiries.withPositions}
          expiriesWithOrder={decoratedExpiries.withOrders}
          onSelectExpiry={onSelectExpiry}
        />
      </Padding>

      <MobileOptionsChainContent
        filteredRows={filteredRows}
        positionsMap={positionsMap}
        onTrade={onTrade}
        optionTypeFilter={optionTypeFilter}
        side={side}
        asset={market.asset}
        currentIndexPrice={currentIndexPrice}
        bottomBarRef={bottomBarRef}
        scrollableContainerRef={scrollableContainerRef}
        isBottomBarShowing={isBottomBarShowing}
        onScrollToPortfolioTray={onScrollToPortfolioTray}
      />

      <MobileTradeFormModal
        instrumentType={InstrumentTypeResponse.Option}
        show={showTradeForm}
        onHide={() => setShowTradeForm(false)}
        selectedInstrument={selectedInstrument}
      />
    </OptionsChainContainer>
  );
}

export default MobileOptionsChain;
